import { router } from "@inertiajs/react";
import { useState, useEffect } from "react";
import {
  Settings as SettingsIcon,
  AlertTriangle,
  RefreshCw,
  Edit,
  Hash,
  Shield,
  CheckCircle2,
  FileCode,
  FolderGit,
} from "lucide-react";
import Layout from "../Layout";
import { BreadcrumbNavigation } from "../../components/BreadcrumbNavigation";
import { PageHeader } from "../../components/PageHeader";
import { SectionCard } from "../../components/SectionCard";
import { IconRow } from "../../components/IconRow";
import { TerraformStateRow } from "../../components/TerraformStateRow";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { CopyButton } from "../../components/ui/CopyButton";
import { EmptyState } from "../../components/ui/EmptyState";
import ConfirmationModal from "../../components/ui/ConfirmationModal";
import RenameAwsAccountModal from "../../components/RenameAwsAccountModal";
import { useToast } from "../../components/ui/toast";
import { NotificationSettingsSection } from "../../components/NotificationSettingsSection";

interface Workspace {
  name: string;
  slug: string;
  has_github_repos: boolean;
}

interface NotificationSetting {
  id: number;
  platform: string;
  destination: string;
  metadata: {
    channel_name: string;
  };
}

interface GitHubRepository {
  id: number;
  uuid: string;
  full_name: string;
  owner: string;
  name: string;
}

interface TerraformState {
  id: number;
  backend_type: "s3" | "github";
  s3_bucket_name: string | null;
  s3_region: string | null;
  s3_key: string | null;
  github_state_filename: string | null;
  github_repository: GitHubRepository;
}

interface AwsAccountSettingsProps {
  uuid: string;
  display_name: string;
  account_id: string;
  role_arn: string;
  status: "untested" | "connected" | "error";
  last_tested_at: string | null;
  last_error: string | null;
  workspace: Workspace;
  can_edit: boolean;
  can_delete: boolean;
  notification_settings: NotificationSetting[];
  integrations: {
    slack_connected: boolean;
  };
  terraform_states: TerraformState[];
}

export default function AwsAccountSettings({
  uuid,
  display_name,
  account_id,
  role_arn,
  status,
  last_tested_at,
  last_error,
  workspace,
  can_edit,
  can_delete,
  notification_settings,
  integrations,
  terraform_states,
}: AwsAccountSettingsProps) {
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [stateToDelete, setStateToDelete] = useState<TerraformState | null>(null);
  const stateToDeleteLabel = stateToDelete?.backend_type === "s3"
    ? `${stateToDelete?.s3_bucket_name}/${stateToDelete?.s3_key}`
    : stateToDelete?.github_state_filename;
  const [isDeletingState, setIsDeletingState] = useState(false);
  const { showToast } = useToast();

  const breadcrumbs = [
    { label: "Workspaces", href: "/workspaces" },
    { label: workspace.name, href: `/workspaces/${workspace.slug}` },
    { label: display_name, href: `/workspaces/${workspace.slug}/aws_accounts/${uuid}` },
    { label: "Settings" },
  ];

  const [lastTestedLabel, setLastTestedLabel] = useState("");

  useEffect(() => {
    if (!last_tested_at) {
      setLastTestedLabel("Never");
      return;
    }
    const date = new Date(last_tested_at);
    const now = new Date();
    const diffInHours = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60)
    );

    if (diffInHours < 1) setLastTestedLabel("< 1h ago");
    else if (diffInHours < 24) setLastTestedLabel(`${diffInHours}h ago`);
    else setLastTestedLabel(`${Math.floor(diffInHours / 24)}d ago`);
  }, [last_tested_at]);

  const handleTestConnection = async () => {
    setIsTesting(true);
    try {
      const response = await fetch(
        `/workspaces/${workspace.slug}/aws_accounts/${uuid}/retest`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute("content") || "",
          },
        }
      );

      const data = await response.json();

      if (data.success) {
        showToast("success", "Connection test successful");
        router.reload({ only: ["status", "last_tested_at", "last_error"] });
      } else {
        showToast("error", `Connection failed: ${data.error}`);
        router.reload({ only: ["status", "last_tested_at", "last_error"] });
      }
    } catch {
      showToast("error", "Failed to test connection");
    } finally {
      setIsTesting(false);
    }
  };

  const handleDelete = () => {
    setIsDeleting(true);

    router.delete(`/workspaces/${workspace.slug}/aws_accounts/${uuid}`, {
      onSuccess: () => {
        setIsDeleting(false);
        setIsDeleteModalOpen(false);
      },
      onError: (errors) => {
        showToast("error", Object.values(errors).join(", ") || "Failed to delete AWS account");
        setIsDeleting(false);
        setIsDeleteModalOpen(false);
      },
    });
  };

  const handleDeleteState = () => {
    if (!stateToDelete) return;

    setIsDeletingState(true);

    router.delete(
      `/workspaces/${workspace.slug}/aws_accounts/${uuid}/terraform_states/${stateToDelete.id}`,
      {
        onSuccess: () => {
          showToast("success", "Infrastructure state removed successfully");
          setStateToDelete(null);
          setIsDeletingState(false);
        },
        onError: (errors) => {
          showToast("error", Object.values(errors).join(", ") || "Failed to remove infrastructure state");
          setIsDeletingState(false);
        },
      }
    );
  };

  return (
    <Layout title={`Settings - ${display_name}`}>
      <section className="px-4 sm:px-6 lg:px-8 py-16 pt-40">
        <div className="max-w-4xl mx-auto">
          <BreadcrumbNavigation items={breadcrumbs} />

          <PageHeader
            title={`${display_name} Settings`}
            actions={
              can_edit && (
                <button
                  onClick={() => setIsRenameModalOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-zinc-800 text-gray-700 dark:text-gray-200 text-xs font-medium rounded-full border border-gray-300 dark:border-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-700 transition-colors"
                >
                  <Edit className="w-4 h-4" />
                  Rename
                </button>
              )
            }
          />

          {/* Account Information Section */}
          <SectionCard
            title="Account information"
            icon={SettingsIcon}
            action={
              <button
                onClick={handleTestConnection}
                disabled={isTesting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-zinc-800 text-gray-700 dark:text-gray-200 text-xs font-medium rounded-full border border-gray-300 dark:border-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isTesting ? "animate-spin" : ""}`} />
                {isTesting ? "Testing..." : "Test"}
              </button>
            }
            footer={lastTestedLabel ? `Last tested: ${lastTestedLabel}` : undefined}
          >
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              <IconRow icon={Hash} action={<CopyButton text={account_id} />}>
                <span className="font-mono">{account_id}</span>
              </IconRow>
              <IconRow icon={Shield} action={<CopyButton text={role_arn} />}>
                <span className="font-mono truncate block">{role_arn}</span>
              </IconRow>
              <IconRow icon={CheckCircle2}>
                <StatusBadge status={status} />
              </IconRow>
            </div>

            {last_error && status === "error" && (
              <div className="mt-2 mb-3 p-2.5 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200 dark:border-red-900">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-600 dark:text-red-400" />
                  <span className="text-xs font-medium text-red-900 dark:text-red-100">Error</span>
                </div>
                <p className="text-xs text-red-800 dark:text-red-200">{last_error}</p>
              </div>
            )}
          </SectionCard>

          {/* Infrastructure states section */}
          <SectionCard
            title="Infrastructure states"
            icon={FileCode}
            className="mt-6"
            action={
              can_edit && workspace.has_github_repos ? (
                <button
                  onClick={() => router.visit(`/workspaces/${workspace.slug}/aws_accounts/${uuid}/terraform_states/new`)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary hover:bg-primary/90 text-white text-xs font-medium rounded-full transition-colors"
                >
                  <FileCode className="w-4 h-4" />
                  Connect state
                </button>
              ) : undefined
            }
          >
            {terraform_states.length === 0 ? (
              workspace.has_github_repos ? (
                <EmptyState
                  icon={FileCode}
                  title="No infrastructure state files configured"
                  description="Connect your infrastructure state files to match recommendations with code."
                  actionUrl={`/workspaces/${workspace.slug}/aws_accounts/${uuid}/terraform_states/new`}
                  actionText="Connect state"
                />
              ) : (
                <EmptyState
                  icon={FolderGit}
                  title="Add an infrastructure repository first"
                  description="Infrastructure states connect your cloud account to an infrastructure repository. Add a repository before creating a state."
                  actionUrl={`/workspaces/${workspace.slug}#infrastructure-repositories`}
                  actionText="Add repository"
                />
              )
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {terraform_states.map((state) => (
                  <TerraformStateRow
                    key={state.id}
                    state={state}
                    connectedTo={{
                      label: state.github_repository.full_name,
                      href: `/workspaces/${workspace.slug}/github_repositories/${state.github_repository.uuid}`,
                    }}
                    onRemove={can_edit ? () => setStateToDelete(state) : undefined}
                  />
                ))}
              </div>
            )}
          </SectionCard>

          {/* Notifications Section */}
          <NotificationSettingsSection
            workspaceSlug={workspace.slug}
            awsAccountUuid={uuid}
            notificationSettings={notification_settings}
            integrations={integrations}
            canEdit={can_edit}
          />

          {/* Danger Zone */}
          {can_delete && (
            <SectionCard title="Danger zone" icon={AlertTriangle} variant="danger" className="mt-6">
              <div className="flex items-center justify-between py-3">
                <div>
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Delete AWS account
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                    Permanently remove all associated recommendations and analysis data.
                  </p>
                </div>
                <button
                  onClick={() => setIsDeleteModalOpen(true)}
                  className="flex-shrink-0 ml-4 inline-flex items-center px-3 py-1.5 border border-red-300 dark:border-red-600 text-xs font-medium rounded-full text-red-700 dark:text-red-300 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors cursor-pointer"
                >
                  Delete
                </button>
              </div>
            </SectionCard>
          )}
        </div>
      </section>

      {/* Rename Modal */}
      <RenameAwsAccountModal
        isOpen={isRenameModalOpen}
        onClose={() => setIsRenameModalOpen(false)}
        workspaceSlug={workspace.slug}
        accountUuid={uuid}
        accountName={display_name}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete AWS Account"
        message={`Are you sure you want to delete "${display_name}"? This will permanently remove all recommendations and cannot be undone.`}
        confirmText="Delete Account"
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Delete State Confirmation Modal */}
      <ConfirmationModal
        isOpen={stateToDelete !== null}
        onClose={() => setStateToDelete(null)}
        onConfirm={handleDeleteState}
        title="Remove infrastructure state?"
        message={`This will disconnect "${stateToDeleteLabel}" from this AWS account. You can reconnect it later if needed.`}
        confirmText="Remove state"
        variant="danger"
        isLoading={isDeletingState}
      />
    </Layout>
  );
}
